import dayjs from "dayjs";
import { isValidYyyyMmDd } from "./calendarYmd";
import { formatTimestampForTable } from "./dateDisplay";
import {
  formatDueDate,
  getDueDatePart,
  getDueInstantForComparison,
  isCalendarDateBeforeToday,
} from "./dueDateTime";
import { sortRowsByConfig } from "./sortRowsByConfig";

export const PARTICIPANT_CONTACT_TYPES = [
  "Phone Call",
  "Email",
  "In-Person",
  "Internal Note",
  "SMS",
];

export const PARTICIPANT_CONTACT_TARGETS = ["Student", "Parent / Guardian"];

export const PARTICIPANT_CONTACT_NOTE_MAX = 4000;

/** Staff-wide follow-up / notes queue filter values (Participant Profiles). */
export const PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_ALL = "";
export const PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_OPEN = "open";
export const PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_OVERDUE = "overdue";
export const PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_DONE = "done";
export const PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_HAS_NOTES = "has_notes";

export const PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_OPTIONS = [
  { value: PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_ALL, label: "— All —" },
  { value: PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_OPEN, label: "Open" },
  { value: PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_OVERDUE, label: "Overdue" },
  { value: PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_DONE, label: "Done" },
  { value: PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_HAS_NOTES, label: "Has Notes" },
];

/** True when Follow-up filter shows the contact-note queue instead of profiles. */
export function isParticipantContactFollowUpQueueFilter(value) {
  return (
    value === PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_OPEN ||
    value === PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_OVERDUE ||
    value === PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_DONE ||
    value === PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_HAS_NOTES
  );
}

/** Follow-up status keys for open / done notes. */
export const CONTACT_FOLLOW_UP_STATUS_NONE = "none";
export const CONTACT_FOLLOW_UP_STATUS_OVERDUE = "overdue";
export const CONTACT_FOLLOW_UP_STATUS_DUE_TODAY = "due_today";
export const CONTACT_FOLLOW_UP_STATUS_UPCOMING = "upcoming";
export const CONTACT_FOLLOW_UP_STATUS_DONE = "done";

export const PARTICIPANT_CONTACT_LOG_TABLE_COLUMNS = [
  { id: "created_date", label: "Logged", cellClass: "table-cell-nowrap" },
  { id: "created_by_name", label: "Logged by" },
  { id: "contact_type", label: "Type" },
  { id: "contact_target", label: "Target" },
  { id: "note_details", label: "Note", cellClass: "table-cell-notes" },
  { id: "follow_up_date", label: "Follow-up", cellClass: "table-cell-nowrap" },
  { id: "event_name", label: "Event" },
];

/** Columns for staff-wide open/overdue follow-up queue. */
export const PARTICIPANT_CONTACT_FOLLOW_UP_QUEUE_COLUMNS = [
  { id: "participant", label: "Participant", cellClass: "table-cell-nowrap" },
  { id: "follow_up_date", label: "Follow-up", cellClass: "table-cell-nowrap" },
  { id: "follow_up_status", label: "Status", cellClass: "table-cell-nowrap" },
  { id: "contact_type", label: "Type" },
  { id: "note_details", label: "Note", cellClass: "table-cell-notes" },
  { id: "created_by_name", label: "Logged by" },
  { id: "event_name", label: "Event" },
];

function textOrDash(raw) {
  return raw != null && String(raw).trim() ? String(raw).trim() : "—";
}

/**
 * @param {{ follow_up_date?: unknown, follow_up_completed_date?: unknown }} log
 * @param {{ today?: import("dayjs").Dayjs }} [options]
 * @returns {'none'|'overdue'|'due_today'|'upcoming'|'done'}
 */
export function getContactFollowUpStatus(log, options = {}) {
  if (!log?.follow_up_date) return CONTACT_FOLLOW_UP_STATUS_NONE;
  if (log.follow_up_completed_date) return CONTACT_FOLLOW_UP_STATUS_DONE;
  const ymd = getDueDatePart(log.follow_up_date);
  if (!ymd) return CONTACT_FOLLOW_UP_STATUS_NONE;
  const today = options.today ?? dayjs();
  if (isCalendarDateBeforeToday(log.follow_up_date, { today })) {
    return CONTACT_FOLLOW_UP_STATUS_OVERDUE;
  }
  const todayYmd = today.format("YYYY-MM-DD");
  if (ymd === todayYmd) return CONTACT_FOLLOW_UP_STATUS_DUE_TODAY;
  return CONTACT_FOLLOW_UP_STATUS_UPCOMING;
}

export function isOpenContactFollowUp(log) {
  const status = getContactFollowUpStatus(log);
  return (
    status === CONTACT_FOLLOW_UP_STATUS_OVERDUE ||
    status === CONTACT_FOLLOW_UP_STATUS_DUE_TODAY ||
    status === CONTACT_FOLLOW_UP_STATUS_UPCOMING
  );
}

export function getContactFollowUpStatusLabel(status) {
  switch (status) {
    case CONTACT_FOLLOW_UP_STATUS_OVERDUE:
      return "Overdue";
    case CONTACT_FOLLOW_UP_STATUS_DUE_TODAY:
      return "Due today";
    case CONTACT_FOLLOW_UP_STATUS_UPCOMING:
      return "Upcoming";
    case CONTACT_FOLLOW_UP_STATUS_DONE:
      return "Done";
    default:
      return "";
  }
}

/** Badge colors aligned with Surveys / Events status badges. */
export function getContactFollowUpStatusBadgeStyle(status) {
  switch (status) {
    case CONTACT_FOLLOW_UP_STATUS_OVERDUE:
      return { background: "#fef2f2", color: "#dc2626" };
    case CONTACT_FOLLOW_UP_STATUS_DUE_TODAY:
      return { background: "#fff7ed", color: "#c2410c" };
    case CONTACT_FOLLOW_UP_STATUS_UPCOMING:
      return { background: "#eff6ff", color: "#1d4ed8" };
    case CONTACT_FOLLOW_UP_STATUS_DONE:
      return { background: "#f0fdf4", color: "#16a34a" };
    default:
      return null;
  }
}

export function formatParticipantContactFollowUpName(log) {
  const preferred =
    log?.participant_preferred_name != null ? String(log.participant_preferred_name).trim() : "";
  if (preferred) return preferred;
  const parts = [log?.participant_first_name, log?.participant_last_name]
    .map((x) => (x != null ? String(x).trim() : ""))
    .filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

/** Display string for a History table column. */
export function getParticipantContactLogDisplay(log, colId) {
  switch (colId) {
    case "created_date":
      return formatTimestampForTable(log?.created_date);
    case "created_by_name":
      return textOrDash(log?.created_by_name);
    case "contact_type":
      return textOrDash(log?.contact_type);
    case "contact_target":
      return textOrDash(log?.contact_target);
    case "note_details":
      return textOrDash(log?.note_details);
    case "follow_up_date":
      return log?.follow_up_date ? formatDueDate(log.follow_up_date) || "—" : "—";
    case "event_name":
      return textOrDash(log?.event_name);
    case "participant":
      return formatParticipantContactFollowUpName(log);
    case "follow_up_status":
      return getContactFollowUpStatusLabel(getContactFollowUpStatus(log)) || "—";
    default:
      return "—";
  }
}

/** Sort key for History table (numbers for dates so UTC date-only follow-ups stay on the calendar day). */
function getParticipantContactLogSortValue(log, colId) {
  if (colId === "created_date") {
    const t = log?.created_date ? new Date(log.created_date).getTime() : NaN;
    return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
  }
  if (colId === "follow_up_date") {
    const inst = getDueInstantForComparison(log?.follow_up_date);
    return inst ? inst.valueOf() : Number.NEGATIVE_INFINITY;
  }
  if (colId === "follow_up_status") {
    const order = {
      [CONTACT_FOLLOW_UP_STATUS_OVERDUE]: 0,
      [CONTACT_FOLLOW_UP_STATUS_DUE_TODAY]: 1,
      [CONTACT_FOLLOW_UP_STATUS_UPCOMING]: 2,
      [CONTACT_FOLLOW_UP_STATUS_DONE]: 3,
      [CONTACT_FOLLOW_UP_STATUS_NONE]: 4,
    };
    return order[getContactFollowUpStatus(log)] ?? 4;
  }
  return getParticipantContactLogDisplay(log, colId);
}

export function sortParticipantContactLogRows(rows, sortConfig) {
  if (!sortConfig?.key) return rows;
  const key = sortConfig.key;
  const decorated = rows.map((o) => ({
    __orig: o,
    [key]: getParticipantContactLogSortValue(o, key),
  }));
  const keyTypes =
    key === "created_date" || key === "follow_up_date" || key === "follow_up_status"
      ? { [key]: "number" }
      : {};
  return sortRowsByConfig(decorated, sortConfig, keyTypes).map((r) => r.__orig);
}

export function matchesParticipantContactFollowUpSearch(log, searchTerm) {
  const q = searchTerm != null ? String(searchTerm).trim().toLowerCase() : "";
  if (!q) return true;
  const hay = [
    formatParticipantContactFollowUpName(log),
    log?.contact_type,
    log?.contact_target,
    log?.note_details,
    log?.created_by_name,
    log?.event_name,
    getContactFollowUpStatusLabel(getContactFollowUpStatus(log)),
    log?.follow_up_date ? formatDueDate(log.follow_up_date) : "",
  ]
    .map((x) => (x != null ? String(x).toLowerCase() : ""))
    .join(" ");
  return hay.includes(q);
}

export const EMPTY_PARTICIPANT_CONTACT_FORM = {
  contact_type: "",
  contact_target: "Student",
  note_details: "",
  follow_up_date: "",
};

/**
 * Client-side validation for a contact-log form.
 * @returns {{ ok: true, values: object } | { ok: false, message: string }}
 */
export function validateParticipantContactLogForm(form) {
  const contact_type = form?.contact_type != null ? String(form.contact_type).trim() : "";
  if (!contact_type) return { ok: false, message: "Contact type is required" };
  if (!PARTICIPANT_CONTACT_TYPES.includes(contact_type)) {
    return { ok: false, message: "Contact type is not valid" };
  }

  const contact_target = form?.contact_target != null ? String(form.contact_target).trim() : "";
  if (!contact_target) return { ok: false, message: "Contact target is required" };
  if (!PARTICIPANT_CONTACT_TARGETS.includes(contact_target)) {
    return { ok: false, message: "Contact target is not valid" };
  }

  const note_details = form?.note_details != null ? String(form.note_details).trim() : "";
  if (!note_details) return { ok: false, message: "Note details are required" };
  if (note_details.length > PARTICIPANT_CONTACT_NOTE_MAX) {
    return {
      ok: false,
      message: `Note details must be ${PARTICIPANT_CONTACT_NOTE_MAX} characters or fewer`,
    };
  }

  const follow_up_date = form?.follow_up_date != null ? String(form.follow_up_date).trim() : "";
  if (follow_up_date && !isValidYyyyMmDd(follow_up_date)) {
    return { ok: false, message: "Follow-up date must be YYYY-MM-DD" };
  }

  return {
    ok: true,
    values: {
      contact_type,
      contact_target,
      note_details,
      follow_up_date,
    },
  };
}
